/**
 * Corridor map (PRD §5.5 U-01).
 *
 * react-leaflet renders all monitored corridor segments as GeoJSON
 * polylines, with alert pins at segment centroids. Click pin → open
 * incident side panel (callback prop).
 *
 * Edge offline tile cache: in production, the Leaflet tile layer URL
 * points at a Nginx-cached path the edge box pre-warms. For dev we use
 * OSM standard tiles directly.
 *
 * Pin colours per PRD U-01:
 *   red    → P1 INTRUSION_THIRD_PARTY / LEAK_SIGNAL
 *   amber  → VEHICLE_VIBRATION / MAINTENANCE_NOISE
 *   grey   → UNKNOWN
 */

import { useMemo } from "react";
import {
  CircleMarker,
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

export interface CorridorMapSegment {
  segment_id: string;
  centroid_lat: number;
  centroid_lng: number;
  gis_polyline?: { type: "LineString"; coordinates: [number, number][] } | null;
}

export interface CorridorMapAlert {
  incident_id: string;
  segment_id: string;
  event_type: string;
  confidence: number;
  status: string;
  centroid_lat: number;
  centroid_lng: number;
}

export interface CorridorMapProps {
  centerLat: number;
  centerLng: number;
  zoom?: number;
  segments: CorridorMapSegment[];
  alerts: CorridorMapAlert[];
  onAlertClick?: (incidentId: string) => void;
}

const PIN_COLOURS: Record<string, string> = {
  INTRUSION_THIRD_PARTY: "#ef4444",
  LEAK_SIGNAL: "#ef4444",
  VEHICLE_VIBRATION: "#f59e0b",
  MAINTENANCE_NOISE: "#f59e0b",
  TRAIN_MOVEMENT: "#3b82f6",
  UNKNOWN: "#94a3b8",
};

function colourFor(eventType: string): string {
  return PIN_COLOURS[eventType] ?? "#94a3b8";
}

export function CorridorMap({
  centerLat,
  centerLng,
  zoom = 13,
  segments,
  alerts,
  onAlertClick,
}: CorridorMapProps) {
  const polylines = useMemo(
    () =>
      segments
        .filter((s) => s.gis_polyline)
        .map((s) => ({
          id: s.segment_id,
          // Leaflet expects [lat, lng] but GeoJSON is [lng, lat] — flip it.
          positions: s.gis_polyline!.coordinates.map(
            ([lng, lat]) => [lat, lng] as [number, number],
          ),
        })),
    [segments],
  );

  return (
    <div
      className="w-full h-[400px] rounded-md overflow-hidden border border-slate-800"
      data-testid="corridor-map"
    >
      <MapContainer
        center={[centerLat, centerLng]}
        zoom={zoom}
        className="h-full w-full"
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {polylines.map((line) => (
          <Polyline
            key={line.id}
            positions={line.positions}
            pathOptions={{ color: "#14b8a6", weight: 4 }}
          />
        ))}
        {alerts.map((alert) => (
          <CircleMarker
            key={alert.incident_id}
            center={[alert.centroid_lat, alert.centroid_lng]}
            radius={10}
            pathOptions={{
              color: colourFor(alert.event_type),
              fillColor: colourFor(alert.event_type),
              fillOpacity: 0.85,
              weight: 2,
            }}
            eventHandlers={{
              click: () => onAlertClick?.(alert.incident_id),
            }}
          >
            <Popup>
              <div className="text-xs">
                <div className="font-semibold">{alert.event_type}</div>
                <div>Segment {alert.segment_id}</div>
                <div>Confidence {Math.round(alert.confidence * 100)}%</div>
                <div className="text-slate-500">{alert.status}</div>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}
